import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import API_BASE_URL from "../../config/api";
import { isRaportMwUser } from "../../utils/raportMagazynWarszawaAccess";
import {
  CELNA_FILTRY,
  ETAPY,
  KOLOR_BEZ_CELNA,
  KOLOR_CELNA,
  WSZYSTKIE,
  filtruj,
  formatCzas,
  formatData,
  kolorStatusu,
  kwantyl,
  podsumuj,
  statystykiUzytkownikow,
  zbudujZgloszenia,
} from "./raportMwUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const pad = (n) => String(n).padStart(2, "0");
const dataLokalna = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dniTemu = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return dataLokalna(d);
};

const LIMIT_PRZEBIEGU = 40;

const PRESETY = [
  { etykieta: "Dziś", dni: 0 },
  { etykieta: "7 dni", dni: 6 },
  { etykieta: "30 dni", dni: 29 },
];

const KOLUMNY = [
  { pole: "nrRef", etykieta: "Ref" },
  { pole: "utworzono", etykieta: "Utworzono", format: formatData },
  { pole: "uzytkownik", etykieta: "Użytkownik" },
  { pole: "nadawca", etykieta: "Nadawca" },
  { pole: "celna", etykieta: "Odprawa celna", format: (v) => (v ? "Tak" : "Nie") },
  { pole: "minOczekiwanie", etykieta: "Przyjazd → skanowanie", liczba: true },
  { pole: "minSkanowanie", etykieta: "Skanowanie", opis: "start skanowania → zakończenie skanowania", liczba: true },
  { pole: "minWydanie", etykieta: "Podjazd → wyjazd", liczba: true },
  { pole: "minNaMagazynie", etykieta: "Na magazynie", opis: "start skanowania → wyjazd auta", liczba: true },
  { pole: "minCalosc", etykieta: "Całość", opis: "przyjazd auta → wyjazd auta", liczba: true },
];

const karta = {
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
  border: "1px solid #E1E5EA",
  borderRadius: 2,
};
const podpis = { color: "#5F6B7A", fontSize: 13, lineHeight: 1.45 };

function Kafel({ etykieta, wartosc, podtytul }) {
  return (
    <Paper elevation={0} sx={karta}>
      <Typography sx={{ ...podpis, fontSize: 12 }}>{etykieta}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
        {wartosc}
      </Typography>
      <Typography sx={{ ...podpis, fontSize: 12, minHeight: 18 }}>{podtytul}</Typography>
    </Paper>
  );
}

// Wiersz "etykieta: wartość" w karcie szczegółów (jak w DetailPage).
function Wiersz({ etykieta, wartosc }) {
  const pusta = wartosc == null || wartosc === "";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 0.5,
        borderBottom: "1px solid #F0F2F5",
        fontSize: 14,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box component="span" sx={{ color: "#5F6B7A", fontWeight: 600 }}>
        {etykieta}
      </Box>
      <Box component="span" sx={{ textAlign: "right", color: pusta ? "#BBB" : "#212121" }}>
        {pusta ? "—" : wartosc}
      </Box>
    </Box>
  );
}

function KartaSekcji({ tytul, kolor = "#3B6FB6", children }) {
  return (
    <Paper elevation={0} sx={{ ...karta, gap: 0.5, borderTop: `3px solid ${kolor}` }}>
      <Typography sx={{ fontWeight: 600, color: kolor, mb: 0.5 }}>{tytul}</Typography>
      {children}
    </Paper>
  );
}

const hhmm = (v) => (v ? String(v).slice(-5) : "");

const RaportMagazynWarszawa = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  let username = null;
  try {
    username = token ? jwtDecode(token).username : null;
  } catch (e) {
    username = null;
  }
  const maDostep = isRaportMwUser(username);

  const [od, setOd] = useState(dniTemu(6));
  const [doDaty, setDoDaty] = useState(dniTemu(0));
  const [dane, setDane] = useState({ zgloszenia: [], zmiany: [], obcieto: false });
  const [ladowanie, setLadowanie] = useState(false);
  const [blad, setBlad] = useState(null);

  const [uzytkownicy, setUzytkownicy] = useState([]);
  const [celna, setCelna] = useState(WSZYSTKIE);
  const [tryb, setTryb] = useState("najdluzsze");
  const [wybranaId, setWybranaId] = useState(null);
  const [szukaj, setSzukaj] = useState("");
  const [sort, setSort] = useState({ pole: "utworzono", kierunek: "desc" });
  const [strona, setStrona] = useState(0);
  const [naStronie, setNaStronie] = useState(25);

  const pobierz = useCallback(async () => {
    if (!maDostep || !od || !doDaty) return;
    setLadowanie(true);
    setBlad(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/raport-magazyn-warszawa`, {
        params: { od, do: doDaty },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          "X-Use-New-Db": localStorage.getItem("useNewDb") || "false",
        },
      });
      setDane({
        zgloszenia: response.data?.zgloszenia || [],
        zmiany: response.data?.zmiany || [],
        obcieto: Boolean(response.data?.obcieto),
      });
      setWybranaId(null);
      setStrona(0);
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setBlad(`Nie udało się pobrać danych raportu: ${msg}`);
    } finally {
      setLadowanie(false);
    }
  }, [maDostep, od, doDaty]);

  useEffect(() => {
    pobierz();
  }, [pobierz]);

  const wszystkie = useMemo(
    () => zbudujZgloszenia(dane.zgloszenia, dane.zmiany),
    [dane],
  );
  const dostepniUzytkownicy = useMemo(
    () =>
      [...new Set(wszystkie.map((z) => z.uzytkownik))].sort((a, b) =>
        a.localeCompare(b, "pl"),
      ),
    [wszystkie],
  );

  // Użytkownik wybrany wcześniej, a nieobecny w nowym zakresie dat, znika z filtra.
  useEffect(() => {
    setUzytkownicy((u) => {
      const zostaje = u.filter((x) => dostepniUzytkownicy.includes(x));
      return zostaje.length === u.length ? u : zostaje;
    });
  }, [dostepniUzytkownicy]);

  // Statystyki użytkowników liczone po filtrze celnym, ale bez filtra użytkowników -
  // żeby wykres zawsze pokazywał porównanie całego zespołu.
  const poCelnej = useMemo(
    () => filtruj(wszystkie, { uzytkownicy: [], celna }),
    [wszystkie, celna],
  );
  const wybrane = useMemo(
    () => filtruj(wszystkie, { uzytkownicy, celna }),
    [wszystkie, uzytkownicy, celna],
  );
  const pods = useMemo(() => podsumuj(wybrane), [wybrane]);
  const osoby = useMemo(() => statystykiUzytkownikow(poCelnej), [poCelnej]);

  const wybrana = useMemo(
    () => wszystkie.find((z) => z.id === wybranaId) || null,
    [wszystkie, wybranaId],
  );

  const ustawPreset = (dni) => {
    setOd(dniTemu(dni));
    setDoDaty(dniTemu(0));
  };
  const presetAktywny = (dni) => od === dniTemu(dni) && doDaty === dniTemu(0);

  const przelaczUzytkownika = (u) =>
    setUzytkownicy((obecni) =>
      obecni.includes(u) ? obecni.filter((x) => x !== u) : [...obecni, u],
    );

  /* ---------- wykres: czas wg użytkownika ---------- */
  const wspolneOpcje = { indexAxis: "y", animation: false, responsive: true, maintainAspectRatio: false };

  const daneOsob = {
    labels: osoby.map((o) => `${o.uzytkownik} (${o.liczba})`),
    datasets: [
      {
        label: "Przyjazd → start skanowania",
        data: osoby.map((o) => o.oczekiwanie),
        backgroundColor: osoby.map((o) =>
          !uzytkownicy.length || uzytkownicy.includes(o.uzytkownik) ? ETAPY[0].kolor : `${ETAPY[0].kolor}55`,
        ),
        borderRadius: 4,
        maxBarThickness: 16,
      },
      {
        label: "Skanowanie (start → zakończenie)",
        data: osoby.map((o) => o.skanowanie),
        backgroundColor: osoby.map((o) =>
          !uzytkownicy.length || uzytkownicy.includes(o.uzytkownik) ? ETAPY[1].kolor : `${ETAPY[1].kolor}55`,
        ),
        borderRadius: 4,
        maxBarThickness: 16,
      },
      {
        label: "Podjazd → wyjazd auta",
        data: osoby.map((o) => o.wydanie),
        backgroundColor: osoby.map((o) =>
          !uzytkownicy.length || uzytkownicy.includes(o.uzytkownik) ? ETAPY[3].kolor : `${ETAPY[3].kolor}55`,
        ),
        borderRadius: 4,
        maxBarThickness: 16,
      },
    ],
  };
  const opcjeOsob = {
    ...wspolneOpcje,
    onClick: (evt, elementy) => {
      if (!elementy.length) return;
      const u = osoby[elementy[0].index]?.uzytkownik;
      if (u) przelaczUzytkownika(u);
    },
    plugins: {
      legend: { position: "top", labels: { boxWidth: 12 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const o = osoby[ctx.dataIndex];
            const [n, p90] = [
              [o.oczekiwanieLiczba, o.oczekiwanieP90],
              [o.skanowanieLiczba, o.skanowanieP90],
              [o.wydanieLiczba, o.wydanieP90],
            ][ctx.datasetIndex];
            return `${ctx.dataset.label}: mediana ${formatCzas(ctx.raw)} · typowo do ${formatCzas(p90)} · pomiarów: ${n}`;
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { callback: (v) => formatCzas(v) } },
      y: { grid: { display: false } },
    },
  };

  /* ---------- wykres: przebieg dostaw ---------- */
  // Dostawy bez cła i z odprawą celną pokazujemy w dwóch osobnych sekcjach, żeby
  // od razu było widać, które to które. Limit dotyczy każdej sekcji osobno.
  const wybierzDoPrzebiegu = (celnaFlag) => {
    const lista = wybrane.filter((z) => z.sumaEtapow > 0 && z.celna === celnaFlag);
    lista.sort((a, b) =>
      tryb === "najdluzsze"
        ? b.sumaEtapow - a.sumaEtapow
        : String(b.utworzono).localeCompare(String(a.utworzono)),
    );
    return lista;
  };
  const przebiegBezCla = useMemo(
    () => wybierzDoPrzebiegu(false),
    [wybrane, tryb], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const przebiegCelna = useMemo(
    () => wybierzDoPrzebiegu(true),
    [wybrane, tryb], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const przebieg = useMemo(
    () => [
      ...przebiegBezCla.slice(0, LIMIT_PRZEBIEGU),
      ...przebiegCelna.slice(0, LIMIT_PRZEBIEGU),
    ],
    [przebiegBezCla, przebiegCelna],
  );

  const skalaPrzebiegu = useMemo(() => {
    const sumy = przebieg.map((z) => z.sumaEtapow);
    if (!sumy.length) return 60;
    const max = Math.max(...sumy);
    const cap = Math.max(30, Math.ceil(((kwantyl(sumy, 0.8) || 30) * 1.5) / 15) * 15);
    return Math.min(max, cap);
  }, [przebieg]);

  const danePrzebiegu = (przebieg) => ({
    labels: przebieg.map((z) => `${z.nrRef} · ${(z.nadawca || "").slice(0, 22)}`),
    datasets: ETAPY.map((e) => ({
      label: e.nazwa,
      data: przebieg.map((z) => z.etapy[e.klucz] || 0),
      backgroundColor: e.kolor,
      stack: "czas",
      barThickness: 14,
    })),
  });
  const opcjePrzebiegu = (przebieg) => ({
    ...wspolneOpcje,
    onClick: (evt, elementy) => {
      if (!elementy.length) return;
      const z = przebieg[elementy[0].index];
      if (z) setWybranaId(z.id === wybranaId ? null : z.id);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        filter: (item) => item.raw > 0,
        callbacks: {
          title: (items) => {
            const z = przebieg[items[0]?.dataIndex];
            return z ? `Ref ${z.nrRef} · ${z.uzytkownik}${z.celna ? " · odprawa celna" : ""}` : "";
          },
          label: (ctx) => {
            const e = ETAPY[ctx.datasetIndex];
            return `${e.nazwa}: ${formatCzas(ctx.raw)} (${e.opis})`;
          },
          footer: (items) => {
            const z = przebieg[items[0]?.dataIndex];
            return z ? `Razem: ${formatCzas(z.sumaEtapow)}` : "";
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: skalaPrzebiegu,
        ticks: { callback: (v) => formatCzas(v) },
      },
      y: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: (ctx) => ({
            weight: przebieg[ctx.index]?.id === wybranaId ? "bold" : "normal",
          }),
        },
      },
    },
  });

  /* ---------- tabela ---------- */
  const wierszeTabeli = useMemo(() => {
    const q = szukaj.trim().toLowerCase();
    const lista = q
      ? wybrane.filter((z) =>
          [z.id, z.nrRef, z.numerListu, z.nadawca, z.uzytkownik].join(" ").toLowerCase().includes(q),
        )
      : [...wybrane];
    const kier = sort.kierunek === "asc" ? 1 : -1;
    lista.sort((a, b) => {
      const va = a[sort.pole];
      const vb = b[sort.pole];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number") return (va - vb) * kier;
      if (typeof va === "boolean") return (Number(va) - Number(vb)) * kier;
      return String(va).localeCompare(String(vb), "pl") * kier;
    });
    return lista;
  }, [wybrane, szukaj, sort]);

  const widoczneWiersze = wierszeTabeli.slice(strona * naStronie, strona * naStronie + naStronie);

  const zmienSort = (pole) =>
    setSort((s) =>
      s.pole === pole
        ? { pole, kierunek: s.kierunek === "asc" ? "desc" : "asc" }
        : { pole, kierunek: pole.startsWith("min") ? "desc" : "asc" },
    );

  if (!maDostep) {
    return (
      <Box sx={{ p: 4 }}>
        <Paper elevation={0} sx={{ ...karta, maxWidth: 520 }}>
          <Typography variant="h6">Magazyn Warszawa – czasy obsługi</Typography>
          <Typography sx={podpis}>Nie masz dostępu do tego modułu.</Typography>
          <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/magazynWarszawaNowyHandling")}>
              Wróć do tabeli
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const otworzZgloszenie = (z) =>
    navigate(`/detail/${z.nrRef}/${z.id}`, { state: { from: "raportMagazynWarszawa" } });

  const historiaStatusow = wybrana ? wybrana.pobyty : [];

  // Oś etapów wybranej dostawy: proporcjonalne segmenty.
  const sumaWybranej = wybrana ? wybrana.sumaEtapow : 0;

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Pasek górny */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
            Magazyn Warszawa – czasy obsługi
          </Typography>
        </Box>

        {/* Filtry */}
        <Paper elevation={0} sx={{ ...karta, flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={PRESETY.find((p) => presetAktywny(p.dni))?.dni ?? null} onChange={(e, v) => v != null && ustawPreset(v)}>
            {PRESETY.map((p) => (
              <ToggleButton key={p.dni} value={p.dni}>
                {p.etykieta}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            label="Od"
            type="date"
            size="small"
            value={od}
            onChange={(e) => e.target.value && setOd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Do"
            type="date"
            size="small"
            value={doDaty}
            onChange={(e) => e.target.value && setDoDaty(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            multiple
            size="small"
            options={dostepniUzytkownicy}
            value={uzytkownicy}
            onChange={(e, v) => setUzytkownicy(v)}
            sx={{ minWidth: 280, flex: "1 1 280px", maxWidth: 520 }}
            noOptionsText="Brak użytkowników w tym zakresie"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Użytkownicy"
                placeholder={uzytkownicy.length ? "" : "Wszyscy"}
              />
            )}
          />
          <ToggleButtonGroup size="small" exclusive value={celna} onChange={(e, v) => v && setCelna(v)}>
            {CELNA_FILTRY.map((f) => (
              <ToggleButton key={f.klucz} value={f.klucz}>
                {f.etykieta}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            size="small"
            onClick={() => {
              setUzytkownicy([]);
              setCelna(WSZYSTKIE);
              setWybranaId(null);
            }}
          >
            Wyczyść filtry
          </Button>
          {ladowanie && <CircularProgress size={20} />}
        </Paper>

        {blad && <Alert severity="error">{blad}</Alert>}
        {dane.obcieto && (
          <Alert severity="warning">
            W tym zakresie jest więcej niż 5000 zgłoszeń. Raport pokazuje 5000 najnowszych; zawęź zakres dat.
          </Alert>
        )}
        {!ladowanie && !blad && !wszystkie.length && (
          <Alert severity="info">Brak zgłoszeń Nowy Handling utworzonych w wybranym zakresie dat.</Alert>
        )}

        {wszystkie.length > 0 && (
          <>
            {/* Kafle */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 1.5 }}>
                <Kafel
                  etykieta="Liczba dostaw"
                  wartosc={pods.liczba}
                  podtytul={`z odprawą celną: ${pods.celnych}`}
                />
                <Kafel
                  etykieta="Przyjazd auta → start skanowania"
                  wartosc={formatCzas(pods.oczekiwanie)}
                  podtytul={
                    pods.oczekiwanieLiczba
                      ? `mediana · typowo do ${formatCzas(pods.oczekiwanieP90)} · pomiarów: ${pods.oczekiwanieLiczba}`
                      : "brak pomiarów"
                  }
                />
                <Kafel
                  etykieta="Skanowanie (start → zakończenie)"
                  wartosc={formatCzas(pods.skanowanie)}
                  podtytul={
                    pods.skanowanieLiczba
                      ? `mediana · typowo do ${formatCzas(pods.skanowanieP90)} · pomiarów: ${pods.skanowanieLiczba}`
                      : "brak pomiarów"
                  }
                />
                <Kafel
                  etykieta="Podjazd auta → wyjazd auta"
                  wartosc={formatCzas(pods.wydanie)}
                  podtytul={
                    pods.wydanieLiczba
                      ? `mediana · typowo do ${formatCzas(pods.wydanieP90)} · pomiarów: ${pods.wydanieLiczba}`
                      : "brak pomiarów"
                  }
                />
                <Kafel
                  etykieta="Paczka na magazynie (start skanowania → wyjazd auta)"
                  wartosc={formatCzas(pods.naMagazynie)}
                  podtytul={
                    pods.naMagazynieLiczba
                      ? `mediana · typowo do ${formatCzas(pods.naMagazynieP90)} · pomiarów: ${pods.naMagazynieLiczba}`
                      : "brak pomiarów"
                  }
                />
              </Box>
              <Typography sx={{ ...podpis, fontSize: 12 }}>
                „Typowo do” to 90. percentyl — 9 na 10 dostaw mieści się w tym czasie. Pomarańczowa sekcja i znacznik oznaczają dostawy z odprawą celną: ich czasów nie umiemy jeszcze mierzyć miarodajnie, więc traktuj je jako odchył.
                Filtr „Bez odprawy celnej” daje czyste czasy magazynu.
              </Typography>
            </Box>

            {/* Czas wg użytkownika */}
            <Paper elevation={0} sx={karta}>
              <Typography sx={{ fontWeight: 500 }}>Czas wg użytkownika</Typography>
              <Typography sx={podpis}>
                Mediana oczekiwania na skanowanie, czasu skanowania i czasu wydania dla każdego użytkownika, w nawiasie liczba dostaw. Kliknij słupek, aby dodać lub usunąć użytkownika z filtra.
              </Typography>
              <Box sx={{ height: Math.max(180, osoby.length * 52 + 70) }}>
                <Bar data={daneOsob} options={opcjeOsob} />
              </Box>
            </Paper>

            {/* Przebieg dostaw */}
            <Paper elevation={0} sx={karta}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 500 }}>Przebieg dostaw</Typography>
                <ToggleButtonGroup size="small" exclusive value={tryb} onChange={(e, v) => v && setTryb(v)}>
                  <ToggleButton value="najdluzsze">{LIMIT_PRZEBIEGU} najdłuższych</ToggleButton>
                  <ToggleButton value="najnowsze">{LIMIT_PRZEBIEGU} najnowszych</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography sx={podpis}>
                Każdy pasek to jedna dostawa, rozbita na etapy od przyjazdu auta do jego wyjazdu. Dostawy bez cła i z odprawą celną są w osobnych sekcjach, a kolory etapów są wszędzie takie same. Skala obu wykresów kończy się na {formatCzas(skalaPrzebiegu)}, dłuższe paski są ucięte (pełny czas w podpowiedzi). Kliknij pasek, aby zobaczyć cały przebieg.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                {ETAPY.map((e) => (
                  <Box key={e.klucz} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, color: "#2A3340" }}>
                    <Box component="span" sx={{ width: 14, height: 10, borderRadius: "2px", background: e.kolor }} />
                    {e.nazwa}
                  </Box>
                ))}
              </Box>
              {przebieg.length ? (
                [
                  {
                    klucz: "bez",
                    tytul: "Bez odprawy celnej",
                    opis: "czyste czasy magazynu",
                    kolor: KOLOR_BEZ_CELNA,
                    tlo: "#F5F8FC",
                    wszystkie: przebiegBezCla,
                  },
                  {
                    klucz: "celna",
                    tytul: "Z odprawą celną",
                    opis: "odchył – tych czasów nie umiemy jeszcze mierzyć miarodajnie",
                    kolor: KOLOR_CELNA,
                    tlo: "#FFF6E8",
                    wszystkie: przebiegCelna,
                  },
                ]
                  .filter((g) => g.wszystkie.length > 0)
                  .map((g) => {
                    const lista = g.wszystkie.slice(0, LIMIT_PRZEBIEGU);
                    return (
                      <Box
                        key={g.klucz}
                        sx={{
                          background: g.tlo,
                          borderLeft: `6px solid ${g.kolor}`,
                          borderRadius: 1.5,
                          p: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 16, color: g.kolor, textTransform: "uppercase", letterSpacing: 0.4 }}>
                            {g.tytul}
                          </Typography>
                          <Typography sx={{ ...podpis, fontSize: 12 }}>
                            {g.wszystkie.length > lista.length
                              ? `${lista.length} z ${g.wszystkie.length} dostaw`
                              : `${lista.length} dostaw`}{" "}
                            · {g.opis}
                          </Typography>
                        </Box>
                        <Box sx={{ height: lista.length * 24 + 60 }}>
                          <Bar data={danePrzebiegu(lista)} options={opcjePrzebiegu(lista)} />
                        </Box>
                      </Box>
                    );
                  })
              ) : (
                <Typography sx={podpis}>Brak dostaw z wpisanymi godzinami dla tych filtrów.</Typography>
              )}
            </Paper>

            {/* Cały przebieg wybranej dostawy */}
            <Paper elevation={0} sx={karta}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 500 }}>Przebieg dostawy</Typography>
                {wybrana && (
                  <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => otworzZgloszenie(wybrana)}>
                    Otwórz zgłoszenie
                  </Button>
                )}
              </Box>
              {!wybrana ? (
                <Typography sx={podpis}>
                  Kliknij pasek na wykresie albo wiersz w tabeli, aby zobaczyć pełny przebieg dostawy: godziny, ilości, odprawę celną i historię statusów.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "baseline",
                      gap: 1,
                      p: 1.5,
                      background: "#E3F2FD",
                      borderLeft: "5px solid #1976D2",
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Status realizacji
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0D47A1" }}>
                      {wybrana.statusRealizacji || "—"}
                    </Typography>
                    {wybrana.celna && <Chip size="small" label="Odprawa celna" sx={{ background: KOLOR_CELNA, color: "#fff", ml: 1 }} />}
                  </Box>

                  {/* Oś czasu etapów */}
                  <Box>
                    <Box sx={{ display: "flex", height: 28, borderRadius: 1, overflow: "hidden", background: "#EEF1F4" }}>
                      {ETAPY.map((e) => {
                        const v = wybrana.etapy[e.klucz];
                        if (!v) return null;
                        return (
                          <Box
                            key={e.klucz}
                            title={`${e.nazwa}: ${formatCzas(v)}`}
                            sx={{
                              flexGrow: v,
                              flexBasis: 0,
                              minWidth: 2,
                              background: e.kolor,
                              color: "#fff",
                              fontSize: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              borderRight: "1px solid #fff",
                            }}
                          >
                            {v / sumaWybranej > 0.1 ? formatCzas(v) : ""}
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "2px 18px", mt: 0.75, ...podpis }}>
                      {ETAPY.map((e) => (
                        <span key={e.klucz}>
                          <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: e.kolor, mr: 0.5 }} />
                          {e.nazwa}: <b>{formatCzas(wybrana.etapy[e.klucz])}</b>
                        </span>
                      ))}
                      <span>
                        Całość (przyjazd → wyjazd): <b>{formatCzas(wybrana.minCalosc)}</b>
                      </span>
                      <span>
                        Na magazynie (start skanowania → wyjazd): <b>{formatCzas(wybrana.minNaMagazynie)}</b>
                      </span>
                    </Box>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 1.5, alignItems: "start" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <KartaSekcji tytul="Przyjęcie">
                        <Wiersz etykieta="Data utworzenia" wartosc={formatData(wybrana.utworzono)} />
                        <Wiersz etykieta="Przyjazd auta" wartosc={hhmm(wybrana.przyjazdAuta)} />
                        <Wiersz etykieta="Numer listu przewozowego" wartosc={wybrana.numerListu} />
                        <Wiersz etykieta="Nazwa firmy (nadawca)" wartosc={wybrana.nadawca} />
                        <Wiersz etykieta="Start skanowania" wartosc={hhmm(wybrana.startSkanowania)} />
                      </KartaSekcji>
                      <KartaSekcji tytul="Obsługa">
                        <Wiersz etykieta="Kierunek" wartosc={wybrana.kierunek} />
                        <Wiersz etykieta="Ilość dekl. paczek" wartosc={wybrana.iloscDeklPaczek} />
                        <Wiersz etykieta="Ilość dekl. palet" wartosc={wybrana.iloscDeklPalet} />
                        <Wiersz etykieta="Ilość fakt. paczek" wartosc={wybrana.iloscFaktPaczek} />
                        <Wiersz etykieta="Ilość fakt. palet" wartosc={wybrana.iloscFaktPalet} />
                        <Wiersz etykieta="Zakończenie skanowania" wartosc={hhmm(wybrana.zakonczenieSkanowania)} />
                        <Wiersz etykieta="Problem z przesyłką" wartosc={wybrana.problemZPrzesylka} />
                        <Wiersz etykieta="Towar celny" wartosc={wybrana.towarCelny} />
                      </KartaSekcji>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {wybrana.celna && (
                        <KartaSekcji tytul="Odprawa celna (odchył – czas niemiarodajny)" kolor={KOLOR_CELNA}>
                          <Wiersz etykieta="Osoba przygotowująca odprawę" wartosc={wybrana.osobaPrzygotowujacaOdprawe} />
                          <Wiersz etykieta="MRN" wartosc={wybrana.MRN} />
                          <Wiersz etykieta="Ilość pozycji SAD" wartosc={wybrana.iloscPozycjiSAD} />
                          <Wiersz etykieta="Rewizja" wartosc={wybrana.Rewizja} />
                          <Wiersz etykieta="Serwis lotniczy" wartosc={wybrana.serwisLotniczy} />
                        </KartaSekcji>
                      )}
                      <KartaSekcji tytul="Wydanie towaru">
                        <Wiersz etykieta="Podjazd auta" wartosc={wybrana.podjazdAuta ? formatData(`${wybrana.znaczniki.podjazd}:00`) : ""} />
                        <Wiersz etykieta="Godzina wyjazdu auta" wartosc={wybrana.wyjazdAuta ? formatData(`${wybrana.znaczniki.wyjazd}:00`) : ""} />
                      </KartaSekcji>
                      <KartaSekcji tytul="Historia statusów" kolor="#6F7F90">
                        <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                          <Box component="li" sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1, py: 0.5 }}>
                            <Typography sx={{ ...podpis, fontVariantNumeric: "tabular-nums" }}>{formatData(wybrana.utworzono)}</Typography>
                            <Typography sx={{ fontSize: 14 }}>Utworzono · {wybrana.uzytkownik}</Typography>
                          </Box>
                          {wybrana.historia
                            .filter((h) => h.fieldName === "statusRealizacji")
                            .map((h, i) => {
                              const pobyt = historiaStatusow.find((p) => p.do === h.editedAt && p.nastepny === h.newValue);
                              return (
                                <Box
                                  component="li"
                                  key={`${h.editedAt}-${i}`}
                                  sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1, py: 0.5, borderTop: "1px solid #E9ECF0" }}
                                >
                                  <Typography sx={{ ...podpis, fontVariantNumeric: "tabular-nums" }}>{formatData(h.editedAt)}</Typography>
                                  <Box>
                                    <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: kolorStatusu(h.newValue), mr: 0.75 }} />
                                    <Typography component="span" sx={{ fontSize: 14 }}>
                                      {h.oldValue || "—"} → {h.newValue || "—"} · {h.editedBy || "—"}
                                    </Typography>
                                    {pobyt && (
                                      <Typography sx={{ ...podpis, fontSize: 12 }}>
                                        po {formatCzas(pobyt.minuty)} w „{pobyt.status}”
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                        </Box>
                        {!wybrana.historia.some((h) => h.fieldName === "statusRealizacji") && (
                          <Typography sx={podpis}>Brak zapisanych zmian statusu dla tej dostawy.</Typography>
                        )}
                      </KartaSekcji>
                    </Box>
                  </Box>
                </>
              )}
            </Paper>

            {/* Tabela */}
            <Paper elevation={0} sx={karta}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>Lista dostaw ({wierszeTabeli.length})</Typography>
                  <Typography sx={{ ...podpis, fontSize: 12 }}>
                    „Całość” liczymy od przyjazdu auta do wyjazdu auta (nie od utworzenia zgłoszenia ani od startu skanowania). „Na magazynie” to czas od startu skanowania do wyjazdu auta.
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Szukaj: Ref, list, nadawca, użytkownik…"
                  value={szukaj}
                  onChange={(e) => {
                    setSzukaj(e.target.value);
                    setStrona(0);
                  }}
                  sx={{ minWidth: 280 }}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {KOLUMNY.map((k) => (
                        <TableCell key={k.pole} align={k.liczba ? "right" : "left"} sortDirection={sort.pole === k.pole ? sort.kierunek : false} sx={{ whiteSpace: "nowrap", fontWeight: 500 }} title={k.opis}>
                          <TableSortLabel active={sort.pole === k.pole} direction={sort.pole === k.pole ? sort.kierunek : "asc"} onClick={() => zmienSort(k.pole)}>
                            {k.etykieta}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {widoczneWiersze.map((z) => (
                      <TableRow
                        key={z.id}
                        hover
                        selected={z.id === wybranaId}
                        onClick={() => setWybranaId((obecna) => (obecna === z.id ? null : z.id))}
                        sx={{
                          cursor: "pointer",
                          boxShadow: `inset 4px 0 0 ${z.celna ? KOLOR_CELNA : KOLOR_BEZ_CELNA}`,
                        }}
                      >
                        {KOLUMNY.map((k) => (
                          <TableCell key={k.pole} align={k.liczba ? "right" : "left"} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {k.liczba ? formatCzas(z[k.pole]) : k.format ? k.format(z[k.pole]) : z[k.pole] ?? "—"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[25, 50, 100]}
                count={wierszeTabeli.length}
                page={Math.min(strona, Math.max(0, Math.ceil(wierszeTabeli.length / naStronie) - 1))}
                rowsPerPage={naStronie}
                onPageChange={(e, p) => setStrona(p)}
                onRowsPerPageChange={(e) => {
                  setNaStronie(parseInt(e.target.value, 10));
                  setStrona(0);
                }}
                labelRowsPerPage="Wierszy na stronie"
              />
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default RaportMagazynWarszawa;
